import React, { memo as reactMemo } from 'react';
import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  List,
  styled,
  Box
} from '@mui/material';
import { motion } from 'framer-motion';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useToggle, useTranslator } from '@ianesthesie-kit/core';
import { isParentNav, LeafNav, NavItemData, ParentNav } from '@/config';
import { useIsVerified } from '@/modules/userConfig';

const variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 }
    }
  }
};

const StyledListItemIcon = styled(ListItemIcon)`
  svg {
    color: white;
  }
`;

const UnstyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

interface LeafNavProps {
  navItem: LeafNav;
  currentRoute: string;
  parentPath?: string;
  disabled?: boolean;
  parentDisabled?: boolean;
}

const LeafNavComp = ({
  navItem,
  currentRoute,
  parentPath = '',
  disabled,
  parentDisabled
}: LeafNavProps) => {
  const translate = useTranslator();
  const path = parentPath + navItem.path;

  return (
    <Box component={UnstyledLink} href={path}>
      <ListItemButton
        disabled={disabled ?? parentDisabled}
        selected={currentRoute === path}
        component={motion.li}
        variants={variants}
        whileHover={{ x: 8 }}
        sx={{
          '&.Mui-disabled': { opacity: '0.5!important' }
        }}
      >
        <StyledListItemIcon>{navItem.icon}</StyledListItemIcon>
        <ListItemText primary={translate(navItem.text)} />
      </ListItemButton>
    </Box>
  );
};

interface ParentNavProps {
  navItem: ParentNav;
  currentRoute: string;
  disabled?: boolean;
}

const ParentNavComp = ({ navItem, currentRoute, disabled }: ParentNavProps) => {
  const isVerified = useIsVerified();
  const { toggle, toggledOn: expanded } = useToggle(
    currentRoute.startsWith(navItem.parentPath)
  );
  const translate = useTranslator();

  const prefixPath = (nav: NavItemData): NavItemData => {
    if (isParentNav(nav)) {
      return {
        ...nav,
        parentPath: navItem.parentPath.concat(nav.parentPath)
      };
    }
    return {
      ...nav,
      path: navItem.parentPath.concat(nav.path)
    };
  };

  return (
    <>
      <ListItemButton
        onClick={() => toggle()}
        component={motion.li}
        variants={variants}
      >
        <StyledListItemIcon>{navItem.icon}</StyledListItemIcon>
        <ListItemText primary={translate(navItem.text)} />
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" sx={{ pl: 2 }} disablePadding>
          {navItem.childNavs.map((nav) => (
            <NavigationItem
              key={`${isParentNav(nav) ? nav.parentPath : nav.path}_${
                nav.text
              }`}
              disabled={nav.verifiedOnly && !isVerified}
              parentDisabled={disabled}
              navItem={prefixPath(nav)}
              currentRoute={currentRoute}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export interface NavigationItemProps {
  navItem: NavItemData;
  currentRoute: string;
  disabled?: boolean;
  parentDisabled?: boolean;
}

const UnmemoizedNavigationItem = ({
  navItem,
  currentRoute,
  disabled,
  parentDisabled
}: NavigationItemProps) => {
  if (isParentNav(navItem)) {
    return (
      <ParentNavComp
        disabled={disabled}
        navItem={navItem}
        currentRoute={currentRoute}
      />
    );
  }

  return (
    <LeafNavComp
      parentDisabled={parentDisabled}
      disabled={disabled}
      navItem={navItem}
      currentRoute={currentRoute}
    />
  );
};

export const NavigationItem = reactMemo(
  UnmemoizedNavigationItem,
  (prevProps, nextProps) => {
    // check for translation updates
    if (prevProps.navItem.text !== nextProps.navItem.text) {
      return false;
    }
    const prevRoute = prevProps.currentRoute;
    const nextRoute = nextProps.currentRoute;
    if (prevRoute === nextRoute) {
      return true;
    }
    if (isParentNav(prevProps.navItem)) {
      const { parentPath } = prevProps.navItem;
      return (
        (prevRoute.startsWith(parentPath) &&
          nextRoute.startsWith(parentPath)) ||
        (!prevRoute.startsWith(parentPath) && !nextRoute.startsWith(parentPath))
      );
    }

    const { path } = prevProps.navItem;
    return (
      (prevRoute === path && nextRoute === path) ||
      (prevRoute !== path && nextRoute !== path)
    );
  }
);

NavigationItem.displayName = 'NavigationItem';
